import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import fbIcon from "../../assets/images/fb.jpeg";
import instaIcon from "../../assets/images/instagram.png";
import AuthApi from "../../api/authApi";
import lovestrongLogo from "../../assets/images/logolovestrong.png";
import moment from "moment";
import RetreatApi from "../../api/retreatApi";
import defaultQrCode from "../../assets/images/Lovestrong QR.png";
import TalkWorksheet from "../../components/TalkWorksheet";
import {
  speakerDisplayName,
  talkHeading,
} from "../../utils/talkWorksheet";

const talkOrder = [
  "Holy Matrimony: God, Husband & Wife",
  "The Sacrament of Marriage",
  "Understanding Yourself, Understanding Your Spouse",
  "Communication",
  "Challenges in Marriage",
  "Children and In-Laws",
  "Sex and Marriage",
  "Marital Bliss",
  "Forgiveness",
  "Us vs. the World",
  "Go Forth With God",
];

const Program = () => {
  const { t, i18n } = useTranslation();
  const [roasterData, setRoasterData] = useState([]);
  const [program, setProgram] = useState([]);
  const [data, setData] = useState([]);
  const [endingPageBgColor, setEndingPageBgColor] = useState("#1f4e7a");
  const [contentReady, setContentReady] = useState(false);
  const autoDownloadStarted = useRef(false);
  let authApi = new AuthApi();
  let retreatApi = new RetreatApi();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const isSpanish = data?.language?.toLowerCase() === "spanish";

  useEffect(() => {
    if (data?.language) {
      const lang = data.language.toLowerCase() === "spanish" ? "es" : "en";
      i18n.changeLanguage(lang === "es" ? "spanish" : "english");
      moment.locale(lang);
    }
  }, [data?.language, i18n]);

  useEffect(() => {
    let cancelled = false;
    setContentReady(false);
    Promise.all([
      getAllRoastersInfo(id),
      getProgramDetail(id),
      getRetreatDetail(id),
    ]).finally(() => {
      if (!cancelled) setContentReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Builds a map of section key -> page number.
  // Page 1 = Cover, Page 2 = TOC, Page 3 = Opening Prayer,
  // then each subsequent section that exists adds one more page.
  // Parish Mission is always rendered so always gets a page.
  const buildPageMap = () => {
    const map = {};
    let page = 1;

    map["cover"] = page++; // Page 1
    map["toc"] = page++; // Page 2
    map["openingPrayer"] = page++; // Page 3

    if (roasterData?.[0]?.role?.length > 0) map["retreatLeadTeam"] = page++;
    if (roasterData?.[1]?.speakers?.length > 0) map["speakersTeam"] = page++;
    if (roasterData?.[2]?.services?.length > 0) map["supportTeams"] = page++;

    if (program?.wisdom_from_mt) map["wisdom"] = page++;

    if (program?.guidelines) map["guidelines"] = page++;

    const speakers = roasterData?.[1]?.speakers || [];
    speakers.forEach((_, i) => {
      map[`talk_${i}`] = page++;
    });

    map["parishMission"] = page++; // always rendered
    map["prayerOfProtection"] = page++;
    map["donations"] = page++;

    if (program?.footerImages?.length > 0) {
      program.footerImages.forEach((_, idx) => {
        map[`footer_${idx}`] = page++;
      });
    }

    map["endingPage"] = page++; // always show ending page

    map["total"] = page - 1;
    return map;
  };

  const getRetreatDetail = async (id) => {
    await retreatApi
      .getRetreatDetail({ id: id })
      .then((res) => {
        if (res.success) {
          const { schedule, ...rest } = res.data[0];
          const parsedSchedule = schedule ? JSON.parse(schedule) : [];
          const parsedData = {
            ...rest,
            schedule: parsedSchedule.map((entry) => ({
              date: new Date(entry.date),
              from: entry.from,
              to: entry.to,
            })),
            parish: res.data[0].parishId,
          };
          setData(parsedData);
        } else {
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  const getAllRoastersInfo = async (id) => {
    await authApi
      .getAllRoastersInfo({ rereatId: id })
      .then((response) => {
        const result = response.data;
        if (result && result[1] && result[1].speakers) {
          result[1].speakers.sort((a, b) => {
            const indexA = talkOrder.indexOf(a.talk);
            const indexB = talkOrder.indexOf(b.talk);
            if (indexA === -1 && indexB === -1)
              return a.talk.localeCompare(b.talk);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
        }
        setRoasterData(result);
      })
      .catch((error) => {
        toast.error(error.toString());
      });
  };

  const getProgramDetail = async (id) => {
    await authApi
      .getProgramDetail({ rereatId: id })
      .then((response) => {
        const modifiedData = { ...response.data[0] };
        if (modifiedData.footerImages) {
          modifiedData.footerImages = JSON.parse(modifiedData.footerImages);
        }
        if (modifiedData.endingPageBgColor) {
          setEndingPageBgColor(modifiedData.endingPageBgColor);
        }
        setProgram(modifiedData);
      })
      .catch((error) => {
        toast.error(error.toString());
      });
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.getElementById("program-pdf-content");

      if (!element) {
        toast.error(t("content_not_found_pdf"));
        return;
      }

      toast.info(t("file_downloading"));

      // Create a clone of the element
      const clonedElement = element.cloneNode(true);

      // Helper function to convert image to data URL
      const convertImageToDataURL = (img) => {
        return new Promise((resolve) => {
          if (img.src.startsWith("data:")) {
            resolve(img.src);
            return;
          }

          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const tempImg = new Image();

          tempImg.crossOrigin = "anonymous";

          tempImg.onload = () => {
            try {
              canvas.width = tempImg.naturalWidth || tempImg.width;
              canvas.height = tempImg.naturalHeight || tempImg.height;

              ctx.fillStyle = "#FFFFFF";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(tempImg, 0, 0);

              const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
              resolve(dataUrl);
            } catch (error) {
              console.warn("Failed to convert image:", img.src, error);
              resolve(img.src);
            }
          };

          tempImg.onerror = () => {
            console.warn("Failed to load image:", img.src);
            resolve(img.src);
          };

          const separator = img.src.includes("?") ? "&" : "?";
          tempImg.src = img.src + separator + "cb=" + Date.now();
          setTimeout(() => resolve(img.src), 5000);
        });
      };

      // Process all images in batches
      const images = clonedElement.getElementsByTagName("img");
      const imageArray = Array.from(images);

      const batchSize = 5;
      for (let i = 0; i < imageArray.length; i += batchSize) {
        const batch = imageArray.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (img) => {
            try {
              const dataUrl = await convertImageToDataURL(img);
              img.src = dataUrl;
              img.loading = "eager";
              img.style.maxWidth = "100%";
              img.style.height = "auto";
              img.style.display = "block";
              img.style.margin = "0 auto";
            } catch (error) {
              console.warn("Error processing image:", error);
            }
          }),
        );
      }

      // Style the cloned element
      clonedElement.style.position = "absolute";
      clonedElement.style.left = "-9999px";
      clonedElement.style.top = "0";
      clonedElement.style.width = "1200px";
      clonedElement.style.backgroundColor = "#ffffff";
      clonedElement.style.padding = "40px";
      clonedElement.style.boxSizing = "border-box";
      clonedElement.style.fontFamily = "Arial, sans-serif";

      // Remove HTML page numbers so they don't appear in the PDF
      const htmlPageNumbers =
        clonedElement.querySelectorAll(".html-page-number");
      htmlPageNumbers.forEach((el) => el.remove());

      // Add avoid-break styles to general sections
      const sections = clonedElement.querySelectorAll(
        ".program-section, .table_content_create, .speakersQuestions, .parishes-logos",
      );
      sections.forEach((section) => {
        section.style.pageBreakInside = "avoid";
        section.style.breakInside = "avoid";
        section.style.marginBottom = "30px";
      });

      const roasterContainer = clonedElement.querySelector(
        ".table_Content_Data",
      );

      // ✅ FORCE NEW PAGE: each program_couple section starts on a new page.
      // Skip the first one — it follows naturally after page 1 content,
      // forcing a break there creates a blank page.
      const programCoupleSections =
        clonedElement.querySelectorAll(".program_couple");
      Array.from(programCoupleSections).forEach((section, index) => {
        section.style.paddingTop = "0";
        section.style.marginTop = "0";
        section.setAttribute("data-force-page-break", "true");
      });

      // ✅ FORCE NEW PAGE before SPEAKERS and SUPPORT TEAMS headings (mid-roaster breaks).
      if (roasterContainer) {
        const roasterHeadings = Array.from(
          roasterContainer.querySelectorAll("h3"),
        ).filter((h3) => !h3.classList.contains("serviceTerms"));
        roasterHeadings.slice(1).forEach((h3) => {
          h3.setAttribute("data-force-page-break", "true");
          h3.style.marginTop = "0";
          h3.style.paddingTop = "10px";
        });

        // Each name/role row must never be split across pages
        const tableRows = roasterContainer.querySelectorAll(
          ".table_content_create",
        );
        tableRows.forEach((row) => {
          row.style.pageBreakInside = "avoid";
          row.style.breakInside = "avoid";
          row.style.display = "flex";
          row.style.alignItems = "center";
        });
      }

      // ✅ Cap main header image — full width, fixed max height (same as directory)
      const mainHeaderImg = clonedElement.querySelector(".imageHeader");
      if (mainHeaderImg) {
        mainHeaderImg.style.maxHeight = "1000px";
        mainHeaderImg.style.width = "100%";
        mainHeaderImg.style.objectFit = "cover";
        mainHeaderImg.style.display = "block";
        mainHeaderImg.style.margin = "0 auto";
      }

      // ✅ FORCE NEW PAGE before each footer image wrapper Col
      // Each footer image gets its own page
      const footerImageCols =
        clonedElement.querySelectorAll(".footer-image-col");
      footerImageCols.forEach((col) => {
        col.style.pageBreakInside = "avoid";
        col.style.breakInside = "avoid";
        col.style.marginBottom = "30px";
        col.setAttribute("data-force-page-break", "true");

        // ✅ Cap footer images — full width, fixed max height (same as directory)
        const footerImg = col.querySelector("img");
        if (footerImg) {
          footerImg.style.maxHeight = "1000px";
          footerImg.style.width = "100%";
          footerImg.style.objectFit = "cover";
          footerImg.style.display = "block";
          footerImg.style.margin = "0 auto";
        }
      });

      document.body.appendChild(clonedElement);

      // Wait for layout to settle
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvasScale = 1.5;

      // ✅ Collect ALL forced page-break positions (top edge of each marked element), sorted ascending
      let forcedBreakPositions = [];
      try {
        const parentRect = clonedElement.getBoundingClientRect();
        const forcedElements = clonedElement.querySelectorAll(
          "[data-force-page-break='true']",
        );
        forcedBreakPositions = Array.from(forcedElements)
          .map((el) => {
            const rect = el.getBoundingClientRect();
            return (rect.top - parentRect.top) * canvasScale;
          })
          .sort((a, b) => a - b);
      } catch (e) {
        console.warn("Failed to calculate forced break positions", e);
        forcedBreakPositions = [];
      }

      // Capture image bounds to avoid splitting images across pages
      const clonedRect = clonedElement.getBoundingClientRect();
      const rawImageBounds = Array.from(
        clonedElement.querySelectorAll("img"),
      ).map((img) => {
        const rect = img.getBoundingClientRect();
        return {
          top: rect.top - clonedRect.top,
          bottom: rect.bottom - clonedRect.top,
        };
      });

      // Generate canvas
      const canvas = await html2canvas(clonedElement, {
        scale: canvasScale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 15000,
        windowWidth: 1200,
        scrollX: 0,
        scrollY: 0,
      });

      document.body.removeChild(clonedElement);

      // Generate PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const topPadding = 10;
      const bottomPadding = 20;
      const usablePdfHeight = pdfHeight - topPadding - bottomPadding;
      const pageHeightPixels = (usablePdfHeight * imgWidth) / pdfWidth;

      const canvasScaleForBounds = clonedRect.width
        ? imgWidth / clonedRect.width
        : 1;
      const scaledImageBounds = rawImageBounds.map((bound) => ({
        top: bound.top * canvasScaleForBounds,
        bottom: bound.bottom * canvasScaleForBounds,
      }));

      const isAreaMostlyWhite = (ctx, x, y, width, height) => {
        try {
          const imageData = ctx.getImageData(x, y, width, height);
          const pixels = imageData.data;
          let whitePixels = 0;
          const totalPixels = pixels.length / 4;

          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            if (r > 240 && g > 240 && b > 240) {
              whitePixels++;
            }
          }

          return whitePixels / totalPixels > 0.95;
        } catch (error) {
          return false;
        }
      };

      // ✅ Return the EARLIEST forced break between startY and endY.
      const getForcedBreakBetween = (startY, endY) => {
        let earliest = null;
        for (const breakPos of forcedBreakPositions) {
          if (breakPos > startY + 5 && breakPos < endY) {
            if (earliest === null || breakPos < earliest) {
              earliest = breakPos;
            }
          }
        }
        return earliest;
      };

      const findBestBreakPoint = (
        ctx,
        startY,
        idealBreakY,
        searchRange = 100,
      ) => {
        const scanHeight = 5;
        let bestBreakY = idealBreakY;
        let maxWhiteSpace = 0;

        for (
          let y = idealBreakY - searchRange;
          y <= idealBreakY + searchRange;
          y += scanHeight
        ) {
          if (y < startY || y >= imgHeight) continue;

          const isWhite = isAreaMostlyWhite(ctx, 0, y, imgWidth, scanHeight);

          if (isWhite) {
            const distanceFromIdeal = Math.abs(y - idealBreakY);
            const score = 1 / (distanceFromIdeal + 1);

            if (score > maxWhiteSpace) {
              maxWhiteSpace = score;
              bestBreakY = y;
            }
          }
        }

        if (maxWhiteSpace === 0) {
          bestBreakY = idealBreakY;
        }

        return bestBreakY;
      };

      const ctx = canvas.getContext("2d");
      const pages = [];

      const adjustHeightForImages = (
        startY,
        proposedHeight,
        remainingHeight,
      ) => {
        let adjustedHeight = Math.min(proposedHeight, remainingHeight);
        const endY = startY + adjustedHeight;

        scaledImageBounds.forEach(({ top, bottom }) => {
          const intersects = top < endY && bottom > startY;
          const fullyInside = top >= startY && bottom <= endY;

          if (intersects && !fullyInside) {
            const spaceBefore = Math.max(0, top - startY);
            const includeFull = bottom - startY;

            if (spaceBefore >= pageHeightPixels * 0.25) {
              adjustedHeight = Math.min(adjustedHeight, spaceBefore - 4);
            } else {
              adjustedHeight = Math.max(adjustedHeight, includeFull + 4);
            }
          }
        });

        adjustedHeight = Math.min(adjustedHeight, remainingHeight);
        adjustedHeight = Math.max(
          adjustedHeight,
          Math.min(remainingHeight, pageHeightPixels * 0.4),
        );

        return adjustedHeight;
      };

      // ✅ First pass: calculate all pages, honoring forced breaks first
      let tempY = 0;
      while (tempY < imgHeight) {
        const remainingHeight = imgHeight - tempY;
        let pageContentHeight;

        if (remainingHeight <= pageHeightPixels) {
          pageContentHeight = remainingHeight;
        } else {
          const idealBreakY = tempY + pageHeightPixels;

          const forcedBreak = getForcedBreakBetween(tempY, idealBreakY);

          if (forcedBreak !== null) {
            pageContentHeight = forcedBreak - tempY;

            // Safety: avoid zero-height or tiny pages
            if (pageContentHeight < 50) {
              pageContentHeight = Math.min(pageHeightPixels, remainingHeight);
            }
          } else {
            // Normal smart break
            const searchRange = Math.min(150, pageHeightPixels * 0.3);
            const bestBreakY = findBestBreakPoint(
              ctx,
              tempY,
              idealBreakY,
              searchRange,
            );
            pageContentHeight = bestBreakY - tempY;

            if (pageContentHeight < pageHeightPixels * 0.5) {
              pageContentHeight = Math.min(pageHeightPixels, remainingHeight);
            }

            // Also apply image-aware adjustment for non-forced pages
            pageContentHeight = adjustHeightForImages(
              tempY,
              pageContentHeight,
              remainingHeight,
            );
          }
        }

        pages.push({ startY: tempY, height: pageContentHeight });
        tempY += pageContentHeight;

        if (pages.length > 100) break;
      }

      const totalPages = pages.length;

      // Second pass: generate PDF with page numbers
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        const page = pages[i];
        const pageContentHeight = page.height;

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = pageContentHeight;
        const pageCtx = pageCanvas.getContext("2d");

        pageCtx.fillStyle = "#FFFFFF";
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        pageCtx.drawImage(
          canvas,
          0,
          page.startY,
          imgWidth,
          pageContentHeight,
          0,
          0,
          imgWidth,
          pageContentHeight,
        );

        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.85);
        const renderWidth = pdfWidth;
        const renderHeight = (pageContentHeight * pdfWidth) / imgWidth;

        pdf.addImage(
          pageImgData,
          "JPEG",
          0,
          topPadding,
          renderWidth,
          renderHeight,
        );

        // Add page number at the bottom center
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `${i + 1} / ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, (pdfWidth - textWidth) / 2, pdfHeight - 7);

        pageCanvas.width = 0;
        pageCanvas.height = 0;
      }

      canvas.width = 0;
      canvas.height = 0;

      const fileName = `program-${
        program && program.title
          ? program.title.replace(/[^a-z0-9]/gi, "_")
          : "retreat"
      }.pdf`;

      pdf.save(fileName);
      toast.success(t("file_downloaded"));
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("failed_generate_pdf"));
    }
  };

  useEffect(() => {
    if (searchParams.get("download") !== "1") return;
    if (!contentReady || !data?.id || autoDownloadStarted.current) return;
    autoDownloadStarted.current = true;
    handleDownloadPDF();
  }, [contentReady, data?.id, searchParams]);

  const pageMap = buildPageMap();

  // Renders "N / Total" at the bottom of each section.
  // Captured by html2canvas so it appears in the PDF automatically.
  const PageNumber = ({ pageKey }) => {
    const pageNum = pageMap[pageKey];
    if (!pageNum) return null;
    return (
      <div
        className="html-page-number"
        style={{
          textAlign: "right",
          width: "100%",
          fontSize: "13px",
          color: "#888",
          padding: "8px 0 2px",
          letterSpacing: "0.5px",
        }}
      >
        {pageNum} / {pageMap["total"]}
      </div>
    );
  };

  return (
    <div className="container-scroller mainpannel">
      <div className="container-fluid page-body-wrapper full-page-wrapper">
        <div className="content-wrapper align-items-center auth px-0 program-view-page">
          <div className="row w-100 mx-0">
            <div className="col-lg-10 mx-auto">
              <div className="d-flex justify-content-center my-2">
                <Button
                  onClick={handleDownloadPDF}
                  size="lg"
                  className="px-2 py-2 fw-bold shadow-lg download-btn"
                >
                  {t("download_pdf")}
                </Button>
              </div>
              <div
                id="program-pdf-content"
                className="auth-form-light  main-register py-5 px-4 px-sm-5"
              >
                <Row>
                  {/* ── PAGE 1: COVER ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    className="colorTalks mt-1 p-5 program-section text-center"
                  >
                    <img
                      src={lovestrongLogo}
                      alt=""
                      srcSet=""
                      className="program_logo"
                    />
                    <h4 style={{ color: "#d2b74c", fontWeight: "700" }}>
                      {t("ministry")}
                    </h4>
                    <h4 className="mt-3 mb-3">
                      <em style={{ color: "#d2b74c", fontWeight: "700" }}>
                        {t("live_love_strong")}
                      </em>
                    </h4>
                    {program && program.image1 ? (
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <div className="">
                          <>
                            <img
                              src={program && program.image1}
                              className="imageHeader"
                              alt={t("header")}
                            />
                          </>{" "}
                        </div>
                      </Col>
                    ) : null}
                    <Col
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      className="directoryHeader1 m-0 mb-3"
                    >
                      <div
                        style={{
                          backgroundColor: endingPageBgColor || "#1f4e7a",
                        }}
                      >
                        <p>{t("retreat_program")}</p>
                        <h2>
                          {" "}
                          {isSpanish
                            ? data?.myparish?.spanishParish ||
                              data?.myparish?.parish
                            : data?.myparish?.parish}
                        </h2>
                        <h1 className="mt-2 m-0">
                          {data.dateFrom && data.dateTo ? (
                            <span>
                              {moment(data.dateFrom).isSame(data.dateTo, "day")
                                ? // Same day
                                  `${moment(data.dateFrom)
                                    .tz("America/New_York")
                                    .format("MMM D, YYYY")}`
                                : moment(data.dateFrom).isSame(
                                      data.dateTo,
                                      "month",
                                    )
                                  ? // Same month, show month only once
                                    `${moment(data.dateFrom)
                                      .tz("America/New_York")
                                      .format("MMM D")} - ${moment(data.dateTo)
                                      .tz("America/New_York")
                                      .format("D")}, ${moment(data.dateFrom)
                                      .tz("America/New_York")
                                      .format("YYYY")}`
                                  : // Different months
                                    `${moment(data.dateFrom)
                                      .tz("America/New_York")
                                      .format("MMM D")} - ${moment(data.dateTo)
                                      .tz("America/New_York")
                                      .format("MMM D")}, ${moment(data.dateFrom)
                                      .tz("America/New_York")
                                      .format("YYYY")}`}
                            </span>
                          ) : null}
                        </h1>
                      </div>
                    </Col>
                    <PageNumber pageKey="cover" />
                  </Col>

                  {/* ── PAGE 2: TABLE OF CONTENTS ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    className="colorTalks mt-3 program_couple program-section"
                  >
                    <div className="program-toc-content">
                      <h3 className="program-toc-title">
                        {t("table_of_contents")}
                      </h3>
                      {(() => {
                        const entries = [];
                        let p = 3;

                        entries.push({
                          label: t("opening_prayer"),
                          page: p++,
                          id: "section-openingPrayer",
                        });

                        if (roasterData?.[0]?.role?.length > 0)
                          entries.push({
                            label: t("retreat_lead_team"),
                            page: p++,
                            id: "section-retreatTeam",
                          });

                        if (roasterData?.[1]?.speakers?.length > 0)
                          entries.push({
                            label: t("speakers"),
                            page: p++,
                            id: "section-speakersTeam",
                          });

                        if (roasterData?.[2]?.services?.length > 0)
                          entries.push({
                            label: t("support_teams"),
                            page: p++,
                            id: "section-supportTeams",
                          });

                        if (program?.wisdom_from_mt)
                          entries.push({
                            label: t("wisdom_mother_theresa"),
                            page: p++,
                            id: "section-wisdom",
                          });

                        if (program?.guidelines)
                          entries.push({
                            label: t("guidelines_sharing"),
                            page: p++,
                            id: "section-guidelines",
                          });

                        const speakers = roasterData?.[1]?.speakers || [];
                        speakers.forEach((item, i) => {
                          entries.push({
                            label: `${t("talk")} ${i + 1}: ${isSpanish ? item.spanishTalk || item.talk : item.talk}`,
                            page: p++,
                            id: `section-talk_${i}`,
                          });
                        });

                        entries.push({
                          label: t("our_parish_mission"),
                          page: p++,
                          id: "section-parishMission",
                        });

                        entries.push({
                          label: t("marriage_prayer_of_protection"),
                          page: p++,
                          id: "section-prayerOfProtection",
                        });

                        entries.push({
                          label: t("lovestrong_donations"),
                          page: p++,
                          id: "section-donations",
                        });

                        if (program?.footerImages?.length > 0) {
                          program.footerImages.forEach((_, idx) => {
                            if (idx === 0) {
                              entries.push({
                                label: t("footer_images"),
                                page: p++,
                                id: `section-footer_${idx}`,
                              });
                            } else {
                              entries.push({
                                label: `${t("footer_images")} ${idx + 1}`,
                                page: p++,
                                id: `section-footer_${idx}`,
                              });
                            }
                          });
                        }

                        // Ending Page is always rendered
                        entries.push({
                          label: t("back_cover_page"),
                          page: p++,
                          id: "section-endingPage",
                        });

                        const handleScroll = (id) => {
                          const element = document.getElementById(id);
                          if (element) {
                            element.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }
                        };

                        return entries.map((entry, index) => (
                          <Row
                            key={index}
                            style={{
                              marginBottom: "15px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleScroll(entry.id);
                            }}
                            className="toc-entry"
                          >
                            <Col
                              xs={8}
                              style={{ color: "#007bff", fontWeight: "500" }}
                            >
                              {entry.label}
                            </Col>
                            <Col
                              xs={4}
                              style={{ color: "#007bff", textAlign: "right" }}
                            >
                              {t("page")} {entry.page}
                            </Col>
                          </Row>
                        ));
                      })()}
                    </div>
                    <PageNumber pageKey="toc" />
                  </Col>

                  {/* ── PAGE 3: OPENING PRAYER ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-openingPrayer"
                    className="colorTalks mt-3 program_couple program-section text-center"
                  >
                    {program?.openingPrayer ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: program?.openingPrayer,
                        }}
                      ></div>
                    ) : (
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{ textAlign: "center" }}
                          dangerouslySetInnerHTML={{
                            __html: t("opening_prayer_content"),
                          }}
                        ></div>
                      </div>
                    )}
                    <img
                      src={lovestrongLogo}
                      alt=""
                      className="program_logo mt-3"
                    />
                    <h5 className="mt-2 mb-3">
                      <em style={{ color: "#d2b74c", fontWeight: "700" }}>
                        {t("live_love_strong")}
                      </em>
                    </h5>
                    <PageNumber pageKey="openingPrayer" />
                  </Col>

                  {/* ✅ program_couple class is used by JS to force a new page before this section */}
                  {(roasterData &&
                    roasterData[0] &&
                    roasterData[0].role &&
                    roasterData[0].role.length) ||
                  (roasterData &&
                    roasterData[1] &&
                    roasterData[1].speakers &&
                    roasterData[1].speakers.length) ||
                  (roasterData &&
                    roasterData[2] &&
                    roasterData[2].services.length) ? (
                    <Col
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      id="section-retreatTeam"
                      className="colorTalks mt-3 program_couple program-section"
                      style={{
                        backgroundColor: "rgb(234 232 232)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <Container className="table_Content_Data">
                        {roasterData &&
                        roasterData[0] &&
                        roasterData[0].role &&
                        roasterData[0].role.length ? (
                          <h3 className="mt-5 mb-5 text-center program-roster-heading">
                            {t("retreat_lead_team")}
                          </h3>
                        ) : null}
                        {roasterData &&
                          roasterData[0] &&
                          roasterData[0].role &&
                          roasterData[0].role
                            .filter((item) =>
                              [
                                "Spiritual Director",
                                "Lead",
                                "Co-Lead",
                                "Mentor",
                                "Spiritual Mentor",
                              ].includes(item.role),
                            )
                            .sort(
                              (a, b) =>
                                [
                                  "Spiritual",
                                  "Lead",
                                  "Co-Lead",
                                  "Mentor",
                                  "Spiritual Mentor",
                                ].indexOf(a.role) -
                                [
                                  "Spiritual Director",
                                  "Lead",
                                  "Co-Lead",
                                  "Mentor",
                                  "Spiritual Mentor",
                                ].indexOf(b.role),
                            )
                            .map((item, i) => {
                              return (
                                <Row
                                  key={i}
                                  className="table_content_create mt-1"
                                >
                                  <Col
                                    lg={2}
                                    md={2}
                                    sm={2}
                                    xs={2}
                                    className="mt-3 program-roster-spacer"
                                  ></Col>

                                  <Col
                                    lg={5}
                                    md={5}
                                    sm={5}
                                    xs={5}
                                    className="mt-3"
                                  >
                                    <p className="program-roster-text program-roster-text--bold">
                                      {item.role === "Spiritual Director"
                                        ? isSpanish
                                          ? item.spanishRole
                                          : item.role
                                        : item.role === "Spiritual Mentor"
                                          ? item?.hisFirstName &&
                                            item?.herFirstName
                                            ? isSpanish
                                              ? `${t("couple_suffix")} ${item.spanishRole || item.role}`
                                              : `${item.role}${t("couple_suffix")}`
                                            : isSpanish
                                              ? item.spanishRole
                                              : item.role
                                          : isSpanish
                                            ? `${t("couple_suffix")} ${item.spanishRole || item.role}`
                                            : `${item.role}${t("couple_suffix")}`}
                                    </p>
                                  </Col>

                                  <Col
                                    lg={5}
                                    md={5}
                                    sm={5}
                                    xs={5}
                                    className="mt-3"
                                  >
                                    <p className="program-roster-text">
                                      {item.role === "Spiritual Director"
                                        ? `${item?.title ? `${item.title} ` : ""}${item?.firstName || ""} ${item?.lastName || ""}`
                                        : item.role === "Spiritual Mentor"
                                          ? item?.hisFirstName &&
                                            item?.herFirstName
                                            ? `${item.hisFirstName} & ${item.herFirstName} ${item.hisLastName || ""}`
                                            : item?.hisFirstName
                                              ? `${item.hisFirstName} ${item.hisLastName || ""}`
                                              : item?.herFirstName
                                                ? `${item.herFirstName} ${item.herLastName || ""}`
                                                : ""
                                          : `${item?.hisFirstName || ""}${
                                              item?.hisFirstName &&
                                              item?.herFirstName
                                                ? " & "
                                                : ""
                                            }${item?.herFirstName || ""} ${
                                              item?.hisLastName ||
                                              item?.herLastName ||
                                              ""
                                            }`}
                                    </p>
                                  </Col>
                                </Row>
                              );
                            })}
                        <PageNumber pageKey="retreatLeadTeam" />
                        {roasterData &&
                        roasterData[1] &&
                        roasterData[1].speakers &&
                        roasterData[1].speakers.length ? (
                          <>
                            {" "}
                            <hr />{" "}
                            <h3
                              id="section-speakersTeam"
                              className="mt-5 mb-5 text-center program-roster-heading"
                            >
                              {t("speakers")}
                            </h3>
                          </>
                        ) : null}
                        {roasterData &&
                          roasterData[1] &&
                          roasterData[1].speakers &&
                          roasterData[1].speakers
                            .slice()
                            .sort((a, b) => {
                              const indexA = talkOrder.indexOf(a.talk);
                              const indexB = talkOrder.indexOf(b.talk);
                              if (indexA === -1 && indexB === -1)
                                return a.talk.localeCompare(b.talk);
                              if (indexA === -1) return 1;
                              if (indexB === -1) return -1;
                              return indexA - indexB;
                            })
                            .map((item, i) => {
                              return (
                                <Row
                                  key={i}
                                  className="table_content_create mt-1"
                                >
                                  <Col
                                    lg={2}
                                    md={2}
                                    sm={2}
                                    xs={2}
                                    className="mt-3 program-roster-spacer"
                                  ></Col>

                                  <Col
                                    lg={5}
                                    md={5}
                                    sm={5}
                                    xs={5}
                                    className="mt-3"
                                  >
                                    <p className="program-roster-text program-roster-text--bold">
                                      {isSpanish
                                        ? item.spanishTalk || item.talk
                                        : item.talk}
                                    </p>
                                  </Col>

                                  <Col
                                    lg={5}
                                    md={5}
                                    sm={5}
                                    xs={5}
                                    className="mt-3"
                                  >
                                    <p className="program-roster-text">
                                      {item.role === "Spiritual Director"
                                        ? `${item?.title ? `${item.title} ` : ""}${item?.firstName || ""} ${item?.lastName || ""}`
                                        : item.role === "Spiritual Mentor"
                                          ? item?.hisFirstName &&
                                            item?.herFirstName
                                            ? `${item.hisFirstName} & ${item.herFirstName} ${item.hisLastName || ""}`
                                            : item?.hisFirstName
                                              ? `${item.hisFirstName} ${item.hisLastName || ""}`
                                              : item?.herFirstName
                                                ? `${item.herFirstName} ${item.herLastName || ""}`
                                                : ""
                                          : `${item?.hisFirstName || ""}${
                                              item?.hisFirstName &&
                                              item?.herFirstName
                                                ? " & "
                                                : ""
                                            }${item?.herFirstName || ""} ${
                                              item?.hisLastName ||
                                              item?.herLastName ||
                                              ""
                                            }`}
                                    </p>
                                  </Col>
                                </Row>
                              );
                            })}
                        <PageNumber pageKey="speakersTeam" />
                        {roasterData &&
                        roasterData[2] &&
                        roasterData[2].services.length ? (
                          <>
                            {" "}
                            <hr />
                            <h3
                              id="section-supportTeams"
                              className="mt-5 mb-5 text-center program-roster-heading"
                            >
                              {t("support_teams")}
                            </h3>
                          </>
                        ) : null}
                        {roasterData &&
                          roasterData[2] &&
                          roasterData[2].services &&
                          (() => {
                            const servicesGrouped = {};

                            // Group by service
                            roasterData[2].services.forEach((item) => {
                              if (!servicesGrouped[item.service]) {
                                servicesGrouped[item.service] = [];
                              }
                              servicesGrouped[item.service].push(item);
                            });

                            return Object.keys(servicesGrouped).map(
                              (serviceName, index) => {
                                return (
                                  <React.Fragment key={index}>
                                    {/* SERVICE TITLE */}
                                    <Row className="table_content_create mt-3">
                                      <Col lg={2} className="program-roster-spacer"></Col>
                                      <Col lg={10}>
                                        <h3 className="mt-3 mb-1 serviceTerms program-service-heading">
                                          {isSpanish
                                            ? servicesGrouped[serviceName][0]
                                                .spanishService || serviceName
                                            : serviceName}
                                        </h3>
                                      </Col>
                                    </Row>

                                    {servicesGrouped[serviceName]
                                      .slice()
                                      .sort((a, b) => {
                                        const roleOrder = [
                                          "spiritual director",
                                          "lead",
                                          "co-lead",
                                          "mentor",
                                          "spiritual mentor",
                                          "head",
                                          "co-head",
                                        ];

                                        const normalizeRole = (role) =>
                                          role
                                            ?.toLowerCase()
                                            .replace(/\s+/g, "-");

                                        const getPriority = (role) => {
                                          if (!role) return 999;
                                          const index = roleOrder.indexOf(
                                            normalizeRole(role),
                                          );
                                          return index !== -1 ? index : 998;
                                        };

                                        const priorityA = getPriority(a.role);
                                        const priorityB = getPriority(b.role);

                                        if (priorityA !== priorityB) {
                                          return priorityA - priorityB;
                                        }

                                        const lastNameA = (
                                          a.hisLastName ||
                                          a.herLastName ||
                                          a.lastName ||
                                          ""
                                        ).toLowerCase();

                                        const lastNameB = (
                                          b.hisLastName ||
                                          b.herLastName ||
                                          b.lastName ||
                                          ""
                                        ).toLowerCase();

                                        return lastNameA.localeCompare(
                                          lastNameB,
                                        );
                                      })
                                      .map((item, i) => {
                                        const isBothPresent =
                                          item?.hisFirstName &&
                                          item?.herFirstName;

                                        return (
                                          <Row
                                            className="table_content_create mt-1"
                                            key={i}
                                          >
                                            <Col lg={2} className="program-roster-spacer"></Col>

                                            {/* NAME */}
                                            <Col lg={5} className="mt-3">
                                              <p className="program-roster-text">
                                                <span
                                                  style={{
                                                    marginRight: "10px",
                                                  }}
                                                >
                                                  ●
                                                </span>

                                                {item.role ===
                                                "Spiritual Director"
                                                  ? `${item?.title ? `${item.title} ` : ""}${item?.firstName || ""} ${
                                                      item?.lastName || ""
                                                    }`
                                                  : isBothPresent
                                                    ? `${item.hisFirstName} & ${item.herFirstName} ${
                                                        item.hisLastName || ""
                                                      }`
                                                    : item?.hisFirstName
                                                      ? `${item.hisFirstName} ${
                                                          item.hisLastName || ""
                                                        }`
                                                      : item?.herFirstName
                                                        ? `${item.herFirstName} ${
                                                            item.herLastName ||
                                                            ""
                                                          }`
                                                        : ""}
                                              </p>
                                            </Col>

                                            {/* ROLE */}
                                            <Col lg={5} className="mt-3">
                                              <p className="program-roster-text program-roster-text--bold">
                                                {item.role ===
                                                "Spiritual Director"
                                                  ? isSpanish
                                                    ? item.spanishRole ||
                                                      item.role
                                                    : item.role
                                                  : item.role ===
                                                      "Spiritual Mentor"
                                                    ? isBothPresent
                                                      ? isSpanish
                                                        ? `${t("couple_suffix")} ${item.spanishRole || item.role}`
                                                        : "Spiritual Mentor Couple"
                                                      : isSpanish
                                                        ? item.spanishRole ||
                                                          item.role
                                                        : item.role
                                                    : item.role
                                                      ? isSpanish
                                                        ? `${t("couple_suffix")} ${item.spanishRole || item.role}`
                                                        : `${item.role} Couple`
                                                      : ""}
                                              </p>
                                            </Col>
                                          </Row>
                                        );
                                      })}
                                  </React.Fragment>
                                );
                              },
                            );
                          })()}
                        <PageNumber pageKey="supportTeams" />
                      </Container>
                    </Col>
                  ) : null}
                  {program && program.wisdom_from_mt ? (
                    <Col
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      id="section-wisdom"
                      className="colorTalks mt-3 program_couple program-section"
                    >
                      <div className="howerOption">
                        <h3 className="mb-4 roaster_list">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: program && program.wisdom_from_mt,
                            }}
                          />
                        </h3>
                      </div>
                      <PageNumber pageKey="wisdom" />
                    </Col>
                  ) : null}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-guidelines"
                    className="colorTalks mt-3 p-5 program_couple program-section text-center"
                  >
                    <img
                      src={lovestrongLogo}
                      alt=""
                      srcSet=""
                      className="logo_image1"
                    />
                    <h3 style={{ color: "#d2b74c", fontWeight: "700" }}>
                      {t("ministry")}
                    </h3>
                    <h5 className="mt-2 mb-3">
                      <em style={{ color: "#d2b74c", fontWeight: "700" }}>
                        {t("live_love_strong")}
                      </em>
                    </h5>
                    <div className="howerOption">
                      <h3
                        className="mb-4 roaster_list"
                        style={{
                          textAlign: "left",
                        }}
                      >
                        {program && program.guidelines ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: program && program.guidelines,
                            }}
                          />
                        ) : null}
                      </h3>
                    </div>
                    <PageNumber pageKey="guidelines" />
                  </Col>
                  {roasterData &&
                    roasterData[2] &&
                    roasterData[1].speakers &&
                    roasterData[1].speakers.map((item, i) => {
                      return item ? (
                        <Col
                          key={i}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          id={`section-talk_${i}`}
                          className="colorTalks mt-3 p-5 program_couple program-section text-center talk-worksheet-page"
                        >
                          <TalkWorksheet
                            title={talkHeading(item, {
                              label: t("talk"),
                              index: i,
                              isSpanish,
                            })}
                            speakerName={speakerDisplayName(item)}
                            bannerColor={endingPageBgColor || "#1f4e7a"}
                            content={item.talk_question}
                          />
                          <PageNumber pageKey={`talk_${i}`} />
                        </Col>
                      ) : null;
                    })}

                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-parishMission"
                    className="colorTalks mt-3 p-5 program_couple program-section"
                  >
                    <div
                      className="parishHeading text-center parishNameProg"
                      style={{
                        backgroundColor: endingPageBgColor || "#1f4e7a",
                      }}
                    >
                      <h3>
                        {isSpanish
                          ? program?.spanishParish || program?.parishName
                          : program?.parishName}
                      </h3>
                    </div>

                    <div className="howerOption">
                      <h2
                        className="mb-4 roaster_list"
                        style={{
                          textAlign: "left",
                        }}
                      >
                        {program && program.parishMission ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: program && program.parishMission,
                            }}
                          />
                        ) : (
                          ""
                        )}
                      </h2>
                    </div>
                    <Row className="parishes-logos">
                      <Row className="align-items-start mt-4 justify-content-center">
                        {/* 2. Parish Details */}
                        {(program?.parishName ||
                          program?.parishAddress ||
                          program?.parishCity ||
                          program?.parishState ||
                          program?.parishzip ||
                          program?.parishLink ||
                          program?.parishImageUrl) && (
                          <Col
                            lg={12}
                            md={12}
                            sm={6}
                            xs={12}
                            className="parishDetail text-center"
                          >
                            {program?.parishImageUrl && program?.parishLink ? (
                              <a
                                href={program.parishLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={program.parishImageUrl}
                                  alt={t("parish_logo")}
                                  className="parishLogo"
                                />
                              </a>
                            ) : (
                              program?.parishImageUrl && (
                                <img
                                  src={program.parishImageUrl}
                                  alt="Parish Logo"
                                  className="parishLogo"
                                />
                              )
                            )}

                            {program?.parishName && (
                              <h3 className="mt-2">
                                {isSpanish
                                  ? program?.spanishParish ||
                                    program?.parishName
                                  : program?.parishName}
                              </h3>
                            )}

                            <h5>
                              {[
                                program?.parishAddress,
                                program?.parishCity,
                                program?.parishState,
                                program?.parishzip,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </h5>

                            {program?.parishLink && (
                              <h5>
                                <a
                                  href={program.parishLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {program.parishLink}
                                </a>
                              </h5>
                            )}
                          </Col>
                        )}
                      </Row>

                      <Col
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        className="mt-3 mb-3 text-center"
                      >
                        <img
                          src={lovestrongLogo}
                          alt=""
                          srcSet=""
                          className="logo_image5"
                        />
                        <h5 className="mt-2 mb-3">
                          <em style={{ color: "#d2b74c", fontWeight: "700" }}>
                            {t("live_love_strong")}
                          </em>
                        </h5>
                        <a
                          href="https://lovestrong.koolderbyacademy.com"
                          style={{
                            textDecoration: "none",
                          }}
                        >
                          <div className="lovestrongLinkWrap">
                            <div className="lovestrongLinkLine" />
                            <h4
                              style={{
                                color: "black",
                                fontWeight: "700",
                                textDecoration: "none",
                                margin: 0,
                              }}
                              className="lovestrongLink"
                            >
                              www.LOVESTRONGMarriage.com
                            </h4>
                          </div>
                        </a>
                      </Col>
                    </Row>
                    <Row className="align-items-start mt-4 justify-content-center">
                      <Col lg={2} md={2} sm={6} xs={12} className="text-center">
                        {program?.fbLink ? (
                          <a
                            href={program.fbLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={fbIcon}
                              alt={t("facebook_link")}
                              className="logo_image1 socialLinkIconFacebook"
                            />
                          </a>
                        ) : (
                          <img
                            src={fbIcon}
                            alt={t("facebook_link")}
                            className="logo_image1 socialLinkIconFacebook"
                          />
                        )}
                      </Col>

                      <Col lg={2} md={2} sm={6} xs={12} className="text-center">
                        {program?.instaLink ? (
                          <a
                            href={program.instaLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={instaIcon}
                              alt={t("instagram_link")}
                              className="logo_image1 socialLinkIcon"
                            />
                          </a>
                        ) : (
                          <img
                            src={instaIcon}
                            alt={t("instagram_link")}
                            className="logo_image1 socialLinkIcon"
                          />
                        )}
                      </Col>
                    </Row>
                    <PageNumber pageKey="parishMission" />
                  </Col>

                  {/* ── PRAYER OF PROTECTION ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-prayerOfProtection"
                    className="colorTalks mt-3 p-5 program_couple program-section"
                  >
                    <div className="howerOption">
                      <h2
                        className="roaster_list"
                        style={{
                          textAlign: "left",
                        }}
                      >
                        {program?.prayer_of_protection ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: program.prayer_of_protection,
                            }}
                          />
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t("prayer_protection_content"),
                            }}
                          />
                        )}
                      </h2>
                    </div>
                    <div className="text-center">
                      <img
                        src={lovestrongLogo}
                        alt=""
                        className="program_logo mt-1"
                      />
                    </div>
                    <PageNumber pageKey="prayerOfProtection" />
                  </Col>

                  {/* ── DONATIONS ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-donations"
                    className="colorTalks mt-3  p-5 program_couple program-section"
                  >
                    <div className="howerOption">
                      <h2
                        className="mb-4 roaster_list"
                        style={{
                          textAlign: "left",
                        }}
                      >
                        <div className="text-center">
                          <img
                            src={lovestrongLogo}
                            alt=""
                            className="program_logo mt-3"
                          />{" "}
                          <h4
                            style={{
                              color: "#d2b74c",
                              fontWeight: "700",
                            }}
                          >
                            {t("ministry")}
                          </h4>
                          <h4 className="mt-3 mb-4">
                            <em
                              style={{
                                color: "#d2b74c",
                                fontWeight: "700",
                              }}
                            >
                              {t("live_love_strong")}
                            </em>
                          </h4>
                        </div>
                        {program?.donations ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: program.donations,
                            }}
                          />
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t("donations_content"),
                            }}
                          />
                        )}
                      </h2>
                      <div className="text-center">
                        <img
                          src={program?.qrCodeImage || defaultQrCode}
                          alt={t("scan_for_more_info")}
                          style={{
                            maxWidth: "200px",
                            height: "auto",
                          }}
                        />
                      </div>
                    </div>
                    <PageNumber pageKey="donations" />
                  </Col>
                  {program &&
                    program.footerImages &&
                    program.footerImages.length > 0 &&
                    program.footerImages.map((item, index) => (
                      <Col
                        key={index}
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        id={`section-footer_${index}`}
                        className="colorTalks mt-3 p-5 howerOption program-section footer-image-col"
                      >
                        <div className="text-center">
                          <img
                            src={item}
                            className="imageHeader"
                            alt={`Footer ${index}`}
                          />
                        </div>
                        <PageNumber pageKey={`footer_${index}`} />
                      </Col>
                    ))}

                  {/* ── ENDING PAGE ── */}
                  <Col
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    id="section-endingPage"
                    className="colorTalks mt-3 p-5 howerOption program-section program-ending-page"
                    style={{
                      backgroundColor: endingPageBgColor,
                    }}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "24px",
                        flex: 1,
                        width: "100%",
                      }}
                    >
                      <div style={{ marginBottom: "20px" }}>
                        <img
                          src={lovestrongLogo}
                          alt={t("lovestrong_marriage")}
                          className="program_logo"
                          style={{
                            maxWidth: "340px",
                            height: "auto",
                            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                          }}
                        />
                      </div>

                      <div
                        style={{
                          backgroundColor: "white",
                          padding: "18px",
                          borderRadius: "10px",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={program?.qrCodeImage || defaultQrCode}
                          alt={t("scan_for_more_info")}
                          style={{
                            maxWidth: "200px",
                            height: "auto",
                            display: "block",
                          }}
                        />
                      </div>
                      <div
                        data-html2canvas-ignore="true"
                        style={{ marginTop: "15px" }}
                      >
                        <Button
                          onClick={() => {
                            const url = program?.qrCodeImage || defaultQrCode;
                            fetch(url)
                              .then((response) => response.blob())
                              .then((blob) => {
                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.download = "LoveStrong_QR_Code.png";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              })
                              .catch(() => {
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = "LoveStrong_QR_Code.png";
                                link.target = "_blank";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              });
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "7px",
                            backgroundColor: "#9b8427",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(155,132,39,0.28)",
                            transition:
                              "background 0.15s, transform 0.1s, box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#b39830")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#9b8427")
                          }
                        >
                          <i className="mdi mdi-download align-middle mt-1"></i>{" "}
                          {t("download_qr_code")}
                        </Button>
                      </div>
                    </div>
                    <PageNumber pageKey="endingPage" />
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Program;
